// @flow
import type { FSA } from 'types/redux';
import type { UserState } from 'reducers/user';
import _ from 'lodash';

import { GET_CONFIG, SET_HAS_DETAILS_HEADER } from 'actions/app';
import * as Actions from 'actions/user';
import availableLanguages from 'data/languages.json';
import grabStorage from 'grab-storage';
import { getLocaleFromBrowserLanguage } from 'grab-locale';
import * as RequestResultCases from 'middlewares/api-middleware';
import { user, defaultUserState } from 'reducers/user';
import { DEFAULT_LOCALE, DEFAULT_API_VERSION } from 'config';

export type AppState = {
  config: any,
  translations: {
    locale: string,
  },
  isLoggedIn: boolean,
  user: UserState,
  hasDetailsHeader: boolean,
};

export const defaultAppConfig = {
  version: DEFAULT_API_VERSION,
};

export function defaultAppState(): AppState {
  const languages = _.keys(availableLanguages);
  let loadedLocale: string = grabStorage.locale;

  if (!languages.includes(loadedLocale)) {
    loadedLocale = DEFAULT_LOCALE;
  }

  grabStorage.locale =
    loadedLocale ||
    getLocaleFromBrowserLanguage({
      DEFAULT_LOCALE,
      availableLocales: _.keys(availableLanguages),
    });

  return {
    config: defaultAppConfig,
    translations: {
      locale: grabStorage.locale.toLowerCase(),
    },
    isLoggedIn: false,
    hasDetailsHeader: false,
    user: defaultUserState(),
  };
}

function app(state: AppState = defaultAppState(), action: FSA): AppState {
  switch (action.type) {
    case GET_CONFIG + RequestResultCases.SUCCESS: {
      if (!action.payload) { return state; }
      return {
        ...state,
        config: {
          ...defaultAppConfig,
          ...action.payload,
        },
      };
    }
    case Actions.COOKIE_LOGIN:
    case Actions.GRAB_LOGIN + RequestResultCases.SUCCESS:
    case Actions.GOOGLE_LOGIN + RequestResultCases.SUCCESS:
      return {
        ...state,
        isLoggedIn: true,
        user: user(state.user, action),
      };
    case Actions.GRAB_LOGIN + RequestResultCases.FAILURE:
      return {
        ...state,
        isLoggedIn: false,
        user: user(state.user, action),
      };
    case Actions.REFRESH_TOKEN + RequestResultCases.FAILURE:
    case Actions.FORCE_RELOGIN:
    case Actions.USER_LOGOUT:
      return {
        ...state,
        isLoggedIn: false,
        user: user(state.user, action),
      };
    case SET_HAS_DETAILS_HEADER:
      return {
        ...state,
        hasDetailsHeader: action.payload,
      };
    default:
      return {
        ...state,
        user: user(state.user, action), // cater for user action
      };
  }
}

export default app;
